import { create } from 'zustand';

// Website configuration interface
interface WebsiteConfig {
    // Navigation configuration
    navigation: {
        mainNav: Array<{
            id: string;
            title: string;
            path: string;
            subNav?: Array<{
                id: string;
                title: string;
                path: string;
            }>;
        }>;
    };

    // Personal information
    personalInfo: {
        name: string;
        title: string;
        description: string;
        avatar: string;
    };

    // Education background
    education: {
        title: string;
        description: string;
        experiences: Array<{
            period: string;
            school: string;
            degree: string;
            major: string;
            gpa?: string;
            ranking?: string; // Ranking information
            honors?: string[]; // Honors and awards
            papers?: string[]; // Published papers
            description?: string; // Learning experience description
            learningPoints?: string[]; // Learning points list
            relevance?: string; // Relevant courses
        }>;
        courses?: Array<{
            name: string;
            description: string;
            relevance: string;
        }>;
        awards: Array<{
            name: string;
            year: string;
            description: string;
        }>;
    };

    // Internship experience
    internships: Array<{
        id: string;
        company: string;
        position: string;
        period: string;
        location: string;
        logo: string;
        description: string;
        workContent: { [key: string]: string[] }; // Work content, key is achievement title, value is responsibility list
        skills: string[];
    }>;

    // Personal projects
    projects: Array<{
        id: string;
        title: string;
        description: string;
        slogan: string; // Project slogan for homepage display
        image: string;
        features: string[];
        implementationPath?: string; // Implementation path
        demoUrl?: string;
        githubUrl?: string;
    }>;

    // Book reviews
    bookReviews: Array<{
        title: string;
        author: string;
        cover: string;
        rating: number;
        review: string;
        keyInsights: string[];
    }>;

    // Resume files
    resume: {
        chinese: string;
        english: string;
    };

    // Footer configuration
    footer: {
        statement: string;
        companyLogos: Array<{
            name: string;
            logo: string;
            url?: string;
        }>;
        contact: {
            email: string;
            linkedin?: string;
            github?: string;
        };
    };
}

// Default configuration
const defaultConfig: WebsiteConfig = {
    navigation: {
        mainNav: [
            {
                id: 'projects',
                title: 'Projects',
                path: '/projects',
                subNav: [
                    { id: 'thinker-model', title: 'Thinkers AI', path: '/projects/thinker-model' },
                    { id: 'tech-policy', title: 'Tech Policy Platform', path: '/projects/tech-policy' },
                    // { id: 'info213', title: 'info213 Language Exchange Platform', path: '/projects/info213' },
                    { id: 'barrage-analysis', title: 'Bullet Screen Analysis', path: '/projects/barrage-analysis' }
                ]
            },
            {
                id: 'internships',
                title: 'Internships',
                path: '/internships',
                subNav: [
                    { id: 'meituan', title: 'Meituan - User Product', path: '/internships/meituan' },
                    { id: 'xiaomi', title: 'Xiaomi - Strategy Product', path: '/internships/xiaomi' },
                    { id: 'xueda', title: 'Xueda - B2B Product', path: '/internships/xueda' }
                ]
            },
            {
                id: 'resume',
                title: 'Resume',
                path: '/resume'
            },
            {
                id: 'qa',
                title: 'Q&A',
                path: '/qa'
            }
        ]
    },

    personalInfo: {
        name: 'Ran Zhang',
        title: 'Product Manager',
        description: 'Connecting technology and user experience, focused on creating future AI-driven product value.',
        avatar: '/images/avatar.jpg'
    },

    education: {
        title: 'Education',
        description: 'Through systematic learning, I have established a solid theoretical foundation and practical abilities, laying a solid foundation for career development.',
        experiences: [
            {
                period: '2020-2024',
                school: 'Nanjing Agricultural University',
                degree: 'Bachelor',
                major: 'Information Management and Information Systems',
                // gpa: '3.8/4.0',
                ranking: '1/65',
                honors: ['National Scholarship', 'Second Place in Nanjing N6 Debate Competition', 'Second Prize in Jiangsu Province Computer Design Competition', 'Bronze Award in "Challenge Cup" Jiangsu Province University Student Entrepreneurship Plan Competition'],
                // description: 'Major in Information Management and Information Systems, minor in Economics, cultivated technical thinking and business insight',
                relevance: 'Information System Analysis and Design, Information Survey Methods, Information Behavior Analysis, Information Retrieval Systems, Big Data Fundamentals, Database Systems, Python'
            },
            {
                period: '2024-2027',
                school: 'Nanjing University',
                degree: 'Master',
                major: 'Library and Information Science',
                // gpa: '3.9/4.0',
                // ranking: '2/30',
                papers: ['Liu Jiangfeng, Zhang Ran, Zhang Jundong, Pei Lei. Empowering Computational Research on Intellectual History with Generative AI: Model Construction and Application Exploration[J]. Library Journal, 2025, 44(03):113-127.DOI:10.13663/j.cnki.lj.2025.03.010.', 'Liu Jiangfeng, Zhang Ran, Wang Dongbo, Pei Lei. Tracing the Historical Trajectory of Scientific Integration: AIGC-Enabled Cross-Science Measurement Research[J]. Library and Information Service, 2024, 68(07):137-150.DOI:10.13266/j.issn.0252-3116.2024.07.013.'],
                // description: 'In-depth study of information science and data analysis, mastering user behavior research and product optimization methods',
                relevance: 'Directly studied product design, mastered core skills such as user research and prototype design'
            },
            {
                period: '2024.08-2025.12',
                school: 'UC Berkeley',
                degree: 'Exchange Student',
                major: 'Information Science',
                // gpa: '3.9/4.0',
                // ranking: 'Top 10%',
                // honors: ['Dean\'s List', 'International Student Excellence Award'],
                learningPoints: [
                    'In-depth study of data science and user experience design methodologies',
                    'Participated in cross-cultural academic exchanges to enhance international perspective',
                    'Completed multiple practical projects to accumulate hands-on experience'
                ],
                relevance: 'Introduction to User Experience Design\nPrinciples & Techniques of Data Science\nSeminar and Research in social media'
            }
        ],

        // courses: [
        //     {
        //         name: 'Product Management',
        //         description: 'Learning core skills such as product lifecycle management, requirements analysis, market research',
        //         relevance: 'Directly related course, providing theoretical foundation for product manager career development'
        //     },
        //     {
        //         name: 'User Experience Design',
        //         description: 'Mastering skills such as user research methods, interaction design, usability testing',
        //         relevance: 'Product managers need deep understanding of user experience, this course provides practical methods'
        //     },
        //     {
        //         name: 'Data Science',
        //         description: 'Learning data-driven decision-making methods such as data analysis, machine learning, A/B testing',
        //         relevance: 'Modern product managers need data-driven thinking, this course develops analytical abilities'
        //     }
        // ],
        awards: [
            {
                name: 'Outstanding Graduate',
                year: '2024',
                description: 'Ranked in top 10% overall performance, awarded Outstanding Graduate title'
            },
            {
                name: 'First Prize in Innovation Design Competition',
                year: '2023',
                description: 'Team project won first prize in university-level innovation design competition'
            }
        ]
    },

    internships: [
        {
            id: 'meituan',
            company: 'Meituan',
            position: 'User Product Intern',
            period: '2025.01-2025.04',
            location: 'Shanghai',
            logo: '/images/logos/meituan.png',
            description: 'Responsible for optimizing product features in Meituan Dianping review writing process to enhance user experience and business metrics',
            workContent: {
                'Launched intelligent dish labeling solution to further reduce review writing costs, improve review quality, and strengthen "Intelligence + Review Writing" capability construction under POI': [
                    'To enhance review information dimensions, addressing the low labeled image rate (0.87%), implemented intelligent recommendation to reduce user operation costs, piloted with dishes under food category, built POI-SKU label recognition capability, achieving +2.16pp labeled image rate after launch, labeled image exposure click-through rate +4.63pp compared to overall market'
                ],
                'Under the background of exploring voice expression forms, compatible with personal homepage voice review form, promoted integration of diverse review forms on writing, consumption, and recording sides': [
                    'Ensured historical reviews support voice display and playback functions, designed voice playback logic and resolved audio channel preemption issues, ensuring synchronization and smoothness of user experience across three sides'
                ],
                'Completed user experience optimization in segmented scenarios, optimized review page input functionality and multi-language adaptation for review writing process around long text and international user needs': [
                    'Under the background of compressed original typing area, completed general text input changes on review writing page, added immersive text input function for long copy authors, enhancing overall typing experience',
                    'Adapted to transit visa-free policy, completed English version adaptation of review writing page, designed and implemented English display on first screen and floating ball switching capability by breaking down review writing page composition under different categories'
                ],
                'Explored review completion page optimization directions, extracted competitor incentives and interaction strategies, built reusable modules to enhance user engagement and retention': [
                    'Addressing the issues of single functionality and excessive marketing sense in review completion page, deeply analyzed positive feedback mechanisms of multiple competitor UGC completion pages, proposed iteration directions including personalized animations, review sharing cards, diversified spiritual incentives, material incentives focusing on gameplay penetration, exploring new forms of completion page'
                ]
            },
            skills: ['Product Design', 'Data Analysis', 'User Research', 'Project Management']
        },
        {
            id: 'xiaomi',
            company: 'Xiaomi',
            position: 'Strategy Product Intern',
            period: '2024.07-2024.09',
            location: 'Nanjing',
            logo: '/images/logos/xiaomi.png',
            description: 'Responsible for strategy development in Xiaomi Software Center game section, analyzing market trends and user needs',
            workContent: {
                'Added personalized recall experimental strategy to further improve light game section distribution efficiency and meet personalized user needs, enhancing section recommendation strategy construction': [
                    'Launched personalized recall experimental strategy, recommending games with same tags based on user game tag history in past 30 days, focusing on per capita exposure, per capita distribution, per capita ad ARPU impact data, sorting and recommending games through machine scoring and CTR reverse ranking combination, achieving 2.23% increase in per capita exposure, 7.45% increase in per capita distribution'
                ],
                'After basic section construction was completed, expansion was urgently needed, confirmed "Audience + Landing Scenarios" by traversing opportunity points within app store, expanded scenarios for light game section to increase exposure': [
                    'Assisted in calculating entry estimated revenue and losses, expanded scenarios through competitor detail pages, search scenarios, user tab weight adjustment and homepage hot word cards, increasing section exposure and distribution, achieving broad coverage for all users and focused reach for casual game preference users, achieving section DAU increase from 48K to 59K from July to September, up 23%'
                ],
                'Addressing low section retention rate, added dual replay paths by breaking down quality competitors, effectively improving section game retention': [
                    'By extracting success factors and innovative gameplay from quality competitor "233 Paradise", built light game section replay path, achieving +6.35% section game retention under experiment; proposed iteration directions including building game social circle, game virtualization, external promotion traffic introduction for the section'
                ]
            },
            skills: ['Market Analysis', 'Strategy Development', 'Business Analysis', 'User Research']
        },
        {
            id: 'xueda',
            company: 'Xueda Education',
            position: 'B2B Product Intern',
            period: '2024.02-2024.05',
            location: 'Beijing',
            logo: '/images/logos/xueda.png',
            description: 'Responsible for B2B product feature design in Xueda Education, serving teachers and institutional users',
            workContent: {
                'Built mobile outbound call platform for Xueda Education internal business personnel, deeply understood business personnel follow-up SOP process, optimized follow-up efficiency tools, enhanced process execution': [
                    'Responsible for requirements analysis and product design of system mobile outbound call functionality, reaching average weekly users of 330; optimized "To Follow Up" and "Location" functions in lead/customer module, improving weekly follow-up users by approximately 6% after optimization'
                ],
                'Conducted routine functional and interactive global inventory, provided feedback and follow-up on problematic modules': [
                    'Conducted acceptance for business opportunity module, trial class scheduling, door-to-door list, coordinated test developers for functional corrections, ensuring smooth launch of all modules'
                ]
            },
            skills: ['B2B Product Design', 'Process Optimization', 'Data Visualization', 'Business Understanding']
        }
    ],

    projects: [
        {
            id: 'thinker-model',
            title: 'Thinkers AI',
            description: 'Core Goal: The fundamental goal of this project is to solve the bottleneck problems encountered by traditional computational methods (such as Natural Language Understanding NLP) in intellectual history research - namely high model training costs, poor generalization effects, and interpretation difficulties. We plan to leverage the most cutting-edge Generative AI (AIGC) technology, especially Large Language Models (LLM), to revolutionize the computational processing of intellectual history materials. Product Positioning: We aim to create a vertical domain large model named "Thinkers Model". It is not a general-purpose chatbot, but a deep knowledge service platform focused on the field of Chinese intellectual history. Its core value lies in efficiently and accurately processing and interpreting massive volumes of intellectual history literature (starting with "Biographies of Chinese Thinkers"), breaking down professional knowledge barriers, and providing new, interactive solutions for academic research and cultural dissemination.',
            slogan: 'AI-empowered intellectual history research, revitalizing traditional culture',
            image: '/images/projects/thinker-model.jpg',
            features: [
                'Thematic knowledge Q&A',
                'Intellectual evolution analysis',
                'Cross-cultural intellectual comparison research',
                'Ancient text processing and activation'
            ],
            implementationPath: `
            Throughout the project process, we strategically advance the construction and optimization of this platform in phases:

1. **Phase 1: Data Foundation and Model Selection**

- Data Source Establishment: Selected the "Biographies of Chinese Thinkers" series as the core data source. This is an authoritative, systematic, and content-rich academic monograph series, providing high-quality vertical domain corpus for model training.

- Base Model Selection: Chose the industry-leading and Chinese-friendly "Baichuan2-7B-chat" as our technical foundation. This allows us to build upon existing work rather than starting from scratch, standing on the shoulders of giants for domain customization.

2. **Phase 2: Three-Step Model Construction and Optimization**

- Domain Pre-training: First, we continue pre-training the base model with processed "Biographies" text, enabling it to initially absorb the language style and professional knowledge of the intellectual history domain.

- Supervised Fine-tuning: Innovatively combined ChatGPT with prompt engineering to construct high-quality domain instruction Q&A datasets. Used this dataset for instruction fine-tuning, teaching the model how to understand and answer intellectual history-related questions. In this phase, we also designed data cleaning and similar Q&A pair merging strategies to improve data quality.

- Direct Preference Optimization (DPO): Instead of the traditional complex RLHF, we adopted the more efficient DPO method. By constructing "one-question-multiple-answers" datasets and using ChatGPT assistance with human calibration for preference labeling, we directly optimize the model to make its generated content more aligned with human expert preferences and cognition.

3. **Phase 3: Evaluation, Iteration and Future Prospects**

- Effectiveness Evaluation: Combined machine automatic evaluation (BLEU, ROUGE) with expert manual evaluation, conducted horizontal comparison with mainstream models such as ChatGLM and Qwen, ensuring our "Thinkers Model" has significant advantages in the vertical domain.

- Continuous Iteration: Will continue to expand the corpus, introduce multimodal information (such as images, audio and video), optimize user interaction experience, and continuously pay attention to model ethics issues to ensure content fairness and objectivity.
            `,
            demoUrl: 'https://github.com/ivyzhang1912/Thinkers-AI',
            githubUrl: 'https://github.com/ivyzhang1912/Thinkers-AI'
        },
        {
            id: 'tech-policy',
            title: 'Tech Policy Platform "KeZhengMing"',
            description: 'This project aims to solve problems such as low information utilization efficiency, high reading costs, and difficulty in querying caused by explosive growth of tech policy information. The project collects over 30,000 policy data issued by science and technology departments of provinces, cities, and major cities across the country, developing a tech policy data mobile application platform (mini-program). The platform utilizes Natural Language Processing (NLP) and deep learning technologies to achieve automated summary generation and personalized precise push of tech policies, aiming to help users (especially enterprises) efficiently and accurately obtain and understand relevant policies, thereby improving policy dissemination and execution efficiency.',
            slogan: 'Making policy information accessible, empowering enterprises with precise decisions',
            image: '/images/projects/tech-policy.jpg',
            features: [
                'Personalized precise push of tech policies',
                'Automated summary generation of tech policies',
                'Tech policy database search and query',
                'User personal information management'
            ],
            implementationPath: `
1. **Demand Research and Project Establishment** The project first conducted market demand research, analyzing that in the tech policy information field, users generally face pain points such as information overload, low utilization efficiency, and difficulty in querying. By comparing existing products in the market such as "Policy Express" and "Policy Link", it was found that they have defects such as lack of content summaries, no personalized push, some functions charging fees or containing advertisements. Based on this, the project established the core goal: developing a completely free, ad-free mobile platform, using automation technology to provide users with precise personalized policy push and refined content summaries, to solve existing market pain points and improve user efficiency in obtaining and understanding policies.

2. **Technical Architecture and Core Algorithms** The technical implementation is mainly based on a three-layer architecture of "Mini-program Frontend - Web Server Backend - Database/Model".

- Frontend Implementation: Using WeChat Developer Tools, building mini-program graphical user interface and interaction logic through WXML, WXSS and JavaScript languages.

- Backend Implementation: Using Python language and Flask web application framework to build server, responsible for processing frontend requests, and serving as a bridge for data interaction between mini-program and database and algorithm models.

- Data and Algorithms:

    - Data Processing and Storage: Crawled over 30,000 policy data from provincial and municipal science and technology department official websites, after data cleaning, manual annotation, keyword extraction (TF-IDF algorithm) and summary pre-generation, stored in MySQL relational database.
    - Summary Generation Technology: Adopted deep learning-based generative summary model (referencing Text-Summarizer-Pytorch-Chinese model), which is based on pointer-generator network, capable of understanding text and generating fluent, natural summary content, rather than simply extracting original text.
    - Precise Push Technology: Adopted content-based recommendation algorithm. Extract keywords for each policy through TF-IDF algorithm, and construct a dynamically updated "User Preference Keyword List" based on user browsing history. When pushing, the system calculates the fitness between policies to be pushed and user preference keywords, and recommends policies with highest fitness to users. Meanwhile, to avoid excessive preference convergence, the preference list introduces decay coefficient for regular updates.
            `,
            demoUrl: 'https://github.com/ivyzhang1912/Tech_policy_platform',
            githubUrl: 'https://github.com/ivyzhang1912/Tech_policy_platform'
        },
        {
            id: 'barrage-analysis',
            title: 'Bullet Screen Sentiment Analysis System',
            description: 'This user research reveals key content features that significantly enhance user engagement and satisfaction by analyzing bullet screen data (quantity fluctuations, sentiment tendencies, content) of Bilibili "Medical Doctor" series high-playback videos',
            slogan: 'Understanding bullet screens to help content creation be more precise',
            image: '/images/projects/barrage-analysis.jpg',
            features: [
                'Bullet screen sentiment analysis',
                'Sentiment trend visualization',
                'Video content features',
                'Content optimization suggestions'
            ],
            implementationPath: `
**Core Pain Points & User Needs**:
This research (user research) focuses on common problems in the current medical science popularization video field: some videos are overly entertaining, lack content persuasiveness, or have difficulty attracting and retaining viewers. The research found that users' needs for medical science popularization content are diverse, not just knowledge acquisition (utility), but also entertainment and relaxation (entertainment), emotional resonance (emotional value), and interactive communication (social interaction). Existing science popularization videos often fail to balance and satisfy these composite needs well.

**Market Opportunity & Product/Content Strategy Direction**:
With the development of new media and the improvement of user health awareness, there is huge market demand for high-quality, highly attractive medical science popularization videos. Bullet screen data from platforms such as Bilibili provides a unique, real-time window that can directly observe users' immediate reactions (focus points, emotions, questions) during viewing, which is deep user insight that traditional questionnaires or interviews cannot reach.
Based on this user research, we should develop content strategies to create or optimize medical science popularization video products, making them not only transmit accurate medical knowledge, but also effectively attract, retain and influence target audiences through entertainment, interactivity and emotional resonance.

**Value Proposition**:

- For Content Creators/Platforms: Provide insights based on real user feedback, guide content creation and optimization, improve video playback volume, completion rate, interaction rate and user satisfaction, shaping a professional and popular brand image.

- For Viewers/Users: Provide medical science popularization content that is both authoritative and credible, as well as vivid, interesting, easy to understand and remember, meeting their diverse needs, improving health literacy, and obtaining enjoyable viewing and interactive experiences.

**Key Features/Content Insights from User Research**:

This user research reveals the following key content features that significantly enhance user engagement and satisfaction by analyzing bullet screen data (quantity fluctuations, sentiment tendencies, content) of Bilibili "Medical Doctor" series high-playback videos (can be considered as core elements for future product/content design):

**1. Balancing Entertainment & Utility**:

- User Insight: High bullet screen frequency areas often correspond to humorous expressions, exaggerated expressions or personification techniques in videos (high entertainment), while pure knowledge explanation parts have lower bullet screen volume (high utility but monotonous).

- Product Implication: Successful science popularization videos need to cleverly integrate entertainment elements with professional knowledge. Cannot be boring for the sake of science popularization, nor sacrifice rigor for entertainment. Need to explore better combination methods, such as using vivid cases to explain complex principles.

**2. Dynamic Visual Presentation**:

- User Insight: Low bullet screen frequency areas often correspond to static images or text screenshots, while high frequency areas are mostly dynamic images.

- Product Implication: Visual presentation should be as dynamic as possible, avoiding long static images. Animations, real scene demonstrations, and even creators' vivid body language help attract user attention.

**3. Real-time Interaction Design**:

- User Insight: When creators directly ask questions or guide sharing experiences in videos, bullet screen interactivity significantly increases, satisfying users' social interaction needs.

- Product Implication: Should strategically design interactive segments (such as questions, voting prompts, encouraging sharing) in video scripts, actively guide users to communicate through bullet screens, creating a "simultaneous" sense of community.

**4. Emotional Resonance & Value Sublimation**:

- User Insight: If the ending part of videos can trigger thinking, convey correct values or reveal social phenomena (such as "water health" video ending), it often causes strong user resonance and stimulates secondary dissemination.

- Product Implication: Content should not only be interesting and useful, but also have a "highlight", triggering users' emotional resonance or value recognition at the ending, enhancing content memorability and influence.

**Strategic Recommendations Based on Research**:

Based on the above user research insights, to improve the communication effectiveness and user satisfaction of medical science popularization videos, the following strategies are recommended:
    
**1. Content Style Strategy**: Adopt "informal tutorial style", that is, maintain the rigor and authority of medical content (for example, creator identity certification, citing reliable sources), while integrating humor, narrative (storytelling) elements, using vivid, down-to-earth language, lowering understanding barriers, and enhancing interest.

**2. Presentation Form Strategy**: Focus on dynamic and diverse visual elements. Reasonably use background music, subtitles, and use animations when necessary to assist explanation, but need to pay attention to avoiding excessive flashiness interfering with core information transmission. Balance the primary and secondary relationship between visual effects and information transmission.

**3. Interactive Communication Strategy**: Treat interaction as part of the content. Actively guide user participation (asking questions, initiating discussions) in videos, and actively respond to user feedback (including bullet screens and comments), creating a two-way communication community atmosphere, transforming viewers from passive receivers to active participants and disseminators.

**4. Value Guidance Strategy**: Topics should be close to user concerns, content production needs to carefully consider potential controversial points, and actively spread correct scientific concepts and values. Deepen content communication effectiveness through empathy and value recognition, promoting users' spontaneous sharing.

**5. (Continuous Optimization) Data-driven Strategy**: Normalize bullet screen analysis. Use text mining technology to continuously monitor changes in bullet screen quantity, sentiment, and topics over time (video progress bar), conduct regular reviews, and use user feedback data as an important basis for content iterative optimization.
            `,
            demoUrl: 'https://github.com/ivyzhang1912/Bullet-Screen-Analysis',
            githubUrl: 'https://github.com/ivyzhang1912/Bullet-Screen-Analysis'
        }
    ],

    bookReviews: [
        {
            title: 'Behind the Product',
            author: 'Wang Shimu',
            cover: '/images/books/muhouchanping.jpg',
            rating: 5,
            review: 'This book gave me a more systematic understanding of "behind-the-scenes product thinking". The author not only breaks down the core responsibilities of product managers in strategic, execution, and collaboration dimensions, but also emphasizes the full-chain thinking approach from value insight to solution implementation. Especially the book\'s analysis of demand mining, user growth, MVP validation and iteration mechanisms helped me understand more deeply the underlying logic behind enterprises creating breakthrough products - insight into trends, identifying core user value, and driving growth through experimentation and review.',
            keyInsights: [
                'Real user insights come from problem definition: Product people should not only see what users "say", but also understand why they "think and act this way", gaining insight into the motivations and scenarios behind needs.',
                'Data and intuition are dual engines: Product judgment cannot rely solely on a single dimension - data is a validation tool, while user feedback, business trends, and industry experience are equally important.',
                'Rapid trial and error, continuous iteration is the foundation of growth: Through small steps and quick runs and gray-scale experiments, quickly test assumptions, reduce trial and error costs, and find certainty in uncertainty.',
                'Products ultimately need to be responsible for business: Features are not the goal, growth, retention, efficiency improvement and commercialization capabilities are the core metrics for measuring product success.'
            ]
        },
        {
            title: 'Structural Thinking',
            author: 'Li Zhongqiu',
            cover: '/images/books/jiegousikaoli.jpg',
            rating: 5,
            review: '"Structural Thinking" emphasizes analyzing problems, organizing information, and expressing viewpoints through structured approaches. During reading, I became more aware that clear thinking structure can not only improve communication efficiency, but is also the underlying capability for product judgment and solution quality. Structure is not a template, but a working method that makes information clearer and logic more rigorous.',
            keyInsights: [
                'Conclusion first, then decomposition: Through the approach of "stating results first, then explaining reasons", help information presentation be more persuasive and logic clearer.',
                'Break down problems, reconstruct structure: Any complex problem can be made more systematic in thinking and more directional in solving through the approach of "split + classify + reconstruct".',
                'Make thinking explicit, expression visual: Structural thinking is not only a thinking tool, but also a way to transform implicit thinking into expressible, presentable structures, thereby enhancing expression power.',
                'Application and contextualization: Structural thinking should not stay at the method level, but should be combined with specific contexts (work, reporting, writing) for training and replicated in daily practice.'
            ]
        },
        {
            title: 'ASK YOUR DEVELOPER',
            author: 'Jeff Lawson',
            cover: '/images/books/askyourdeveloper.jpeg',
            rating: 4,
            review: 'In the digital age, every company is actually becoming a "software company". During reading, I re-recognized the relationship between product managers/strategy leaders and development teams: from "issuing requirements - waiting for delivery" to "co-defining problems - creating solutions together". The real value lies in making developers participants in business value, user experience, and strategic growth, not just technical outputters.',
            keyInsights: [
                'Technology and business co-creation culture is the key to success: Making development teams close to users and strategic goals, and forming cross-functional collaboration and shared language culture can unleash the potential of the entire organization.',
                'Iteration and experimentation are competitive advantages: Traditional "plan-execute" model is slow and rigid, excellent modern organizations seize market opportunities through small-scale experiments, rapid feedback, and continuous expansion.',
                'Build core capabilities, selectively outsource: Enterprises should focus on autonomously building "truly differentiated" software capabilities, while other modularizable and serviceable parts can adopt external platforms.']
        }
    ],

    resume: {
        chinese: '/files/resume-zh.pdf',
        english: '/files/resume-en.pdf'
    },

    footer: {
        statement: 'All information displayed on this website is true, including education background, internship experience, project experience, etc. If you have any questions, please feel free to contact me for verification.',
        companyLogos: [
            {
                name: 'Meituan',
                logo: '/images/logos/meituan.png',
                url: 'https://www.meituan.com'
            },
            {
                name: 'Xiaomi',
                logo: '/images/logos/xiaomi.png',
                url: 'https://www.mi.com'
            },
            {
                name: 'Xueda Education',
                logo: '/images/logos/xueda.png',
                url: 'https://www.xueda.com'
            }
        ],
        contact: {
            email: 'ranzhang1912@gmail.com',
            linkedin: 'www.linkedin.com/in/ivy-zhang-a6713a384',
            github: 'https://github.com/ivyzhang1912'
        }
    }
};


// State management
interface DashboardState {
    config: WebsiteConfig;
}

export const useDashboardStore = create<DashboardState>((set, get) => ({
    config: defaultConfig
}));
